import packArtMap from "./gacha-pack-art-map.json"

export type GachaRarity = "common" | "uncommon" | "rare" | "epic"

const RARITIES: readonly GachaRarity[] = ["common", "uncommon", "rare", "epic"]
const UI_BRANCH_POKEMON_50_MACHINE_CODE = "pokemon_50"
const UI_BRANCH_POKEMON_50_MACHINE_IMAGE =
  "/images/gatcha/gacha-station-focus-poster.webp"
const UI_BRANCH_POKEMON_50_MACHINE_VIDEO =
  "/images/gatcha/gacha-station-focus.mp4"

const WATCH_MACHINE_SELECTION_VIDEOS: Record<string, string> =
  packArtMap.machineVideos

// Pack titles for machines whose Collector Crypt name is only the code.
const MACHINE_NAMES: Record<string, string> = packArtMap.machineNames

const RARITY_LABELS: Record<GachaRarity, string> = {
  common: "Common",
  uncommon: "Uncommon",
  rare: "Rare",
  epic: "Epic",
}

const RARITY_COLORS: Record<GachaRarity, string> = {
  common: "text-yellow-400",
  uncommon: "text-green-400",
  rare: "text-blue-400",
  epic: "text-red-400",
}

export function isWatchGachaMachine(machineCode: string) {
  return Object.hasOwn(WATCH_MACHINE_SELECTION_VIDEOS, machineCode)
}

export type GachaTierRange = {
  start?: number
  end?: number
}

export type GachaMachineApi = {
  code: string
  name: string
  shortName?: string
  image?: string
  thumbnailUrl?: string
  videoSrc?: string
  videoHevc?: string
  public?: boolean
  price?: number
  ev?: number | string
  contains?: number | string
  instantBuyback?: number
  freeSpins?: boolean
  turboMode?: boolean
  pointsMultiplier?: number
  odds?: Partial<Record<GachaRarity, number>>
  tierRanges?: Partial<Record<GachaRarity, GachaTierRange>>
  stock?: Partial<Record<GachaRarity, number>>
}

export type GachaStatusApi = {
  status?: string
  isOpen?: boolean | null
}

export type GachaOddsTier = {
  tierLabel: string
  range: string
  chancePercent: string
  chancePercentClassName: string
}

export type GachaMachineView = {
  code: string
  name: string
  label: string
  thumbnailUrl: string
  videoSrc: string
  videoHevc: string
  price: number
  priceLabel: string
  averageValueLabel: string
  instantBuybackPercent: number
  turboMode: boolean
  oddsTiers: GachaOddsTier[]
  commonStock: number
  stockTotal: number
  isOpen: boolean
  isSoldOut: boolean
  isPublic: boolean
}

export type GachaNftAttribute = {
  trait_type?: string
  value?: string | number | boolean
}

export type GachaNftApi = {
  id?: string
  nft_address?: string
  mint?: string
  mint_address?: string
  tokenAddress?: string
  contractAddress?: string
  minted_at?: string
  mintedAt?: string
  mintedTime?: string
  mintTime?: string
  received_at?: string
  receivedAt?: string
  receivedTime?: string
  owner_received_at?: string
  ownerReceivedAt?: string
  transferred_at?: string
  transferredAt?: string
  purchased_at?: string
  purchasedAt?: string
  opened_at?: string
  openedAt?: string
  created_at?: string
  createdAt?: string
  updated_at?: string
  updatedAt?: string
  blockTime?: number
  portfolioOrder?: number
  certNumber?: string
  cert_number?: string
  certificationNumber?: string
  certification_number?: string
  psaCertNumber?: string
  psa_cert_number?: string
  ownerAddress?: string
  owner_address?: string
  vault?: string
  name?: string
  image?: string
  description?: string
  collection?: {
    name?: string
  }
  collectionName?: string
  collection_name?: string
  category?: string
  categoryName?: string
  category_name?: string
  setName?: string
  set_name?: string
  parallel?: string
  insured_value?: number | string
  insuredValue?: number | string
  rarity?: string
  content?: {
    files?: Array<{
      uri?: string
      cc_cdn?: string
      cdn_uri?: string
    }>
    links?: {
      image?: string
      external_url?: string
    }
    metadata?: {
      name?: string
      description?: string
      attributes?: GachaNftAttribute[]
      insuredValue?: number | string
      gradingCompany?: string
      gradeNum?: string
      certNumber?: string
      cert_number?: string
      certificationNumber?: string
      certification_number?: string
      psaCertNumber?: string
      psa_cert_number?: string
      category?: string
      categoryName?: string
      category_name?: string
      setName?: string
      set_name?: string
      parallel?: string
      minted_at?: string
      mintedAt?: string
      received_at?: string
      receivedAt?: string
      transferred_at?: string
      transferredAt?: string
      purchased_at?: string
      purchasedAt?: string
      opened_at?: string
      openedAt?: string
      created_at?: string
      createdAt?: string
    }
  }
}

export type GachaWinnerApi = {
  winner?: string
  prizeWallet?: number
  prizewallet?: number
  nft_address?: string
  nft?: GachaNftApi
  insuredValue?: number | string
  created_at?: string
  memo_slug?: string
  pack_type?: string
  prize_tier?: number
}

export type GachaPrizeCard = {
  itemId: string
  name: string
  meta: string
  price: string
  category?: string
  collection?: string
  setName?: string
  image: string
  href: string
}

export type GachaItemDetail = {
  id: string
  name: string
  fullName: string
  description: string
  collectionName: string
  categoryName: string
  grade: string
  value: string
  frontImageUrl: string
  backImageUrl: string
  certNumber: string
  psaUrl: string
  setName: string
  year: string
  parallel: string
  vault: string
  contractAddress: string
  ownerAddress: string
  externalUrl: string
  attributes: GachaNftAttribute[]
}

export type GachaRecentPull = {
  name: string
  value: string
  collection?: string
  time?: string
  grade?: string
}

export function normalizeGachaMediaUrl(
  value: string | undefined,
  apiBaseUrl: string,
) {
  if (!value) return ""
  if (/^https?:\/\//.test(value)) return value

  const base = apiBaseUrl.replace(/\/$/, "")
  const path = value.startsWith("/") ? value : `/${value}`
  return `${base}${path}`
}

export function formatUsd(value: number | string | undefined) {
  const parsed = typeof value === "string" ? Number(value) : value
  const amount = Number.isFinite(parsed) ? Number(parsed) : 0

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount)
}

export function formatUsdWithCents(value: number | string | undefined) {
  const parsed = typeof value === "string" ? Number(value) : value
  const amount = Number.isFinite(parsed) ? Number(parsed) : 0

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

export function normalizeUsdAmount(value: number | string | undefined) {
  const parsed = typeof value === "string" ? Number(value) : value
  if (!Number.isFinite(parsed)) return 0

  const amount = Number(parsed)
  return amount >= 100000 ? amount / 1_000_000 : amount
}

function formatInsuredValue(value: number | string | undefined) {
  return formatUsd(normalizeUsdAmount(value))
}

function formatPercent(value: number | undefined) {
  if (!Number.isFinite(value)) return "0%"
  return `${Math.round(Number(value) * 100)}%`
}

function formatRange(range: GachaTierRange | undefined) {
  const start = range?.start ?? 0
  const end = range?.end ?? 0
  if (end > 100000) return `${formatUsd(start)}+`
  return `${formatUsd(start)} - ${formatUsd(end)}`
}

export function mapGachaMachine(
  machine: GachaMachineApi,
  options: { apiBaseUrl: string; status?: GachaStatusApi },
): GachaMachineView {
  const usesUiBranchMachineImage =
    machine.code === UI_BRANCH_POKEMON_50_MACHINE_CODE
  const watchSelectionVideoSrc = WATCH_MACHINE_SELECTION_VIDEOS[machine.code]
  const thumbnailUrl = usesUiBranchMachineImage
    ? UI_BRANCH_POKEMON_50_MACHINE_IMAGE
    : normalizeGachaMediaUrl(
        machine.thumbnailUrl || machine.image,
        options.apiBaseUrl,
      )
  const videoSrc = watchSelectionVideoSrc
    ? watchSelectionVideoSrc
    : usesUiBranchMachineImage
      ? UI_BRANCH_POKEMON_50_MACHINE_VIDEO
      : normalizeGachaMediaUrl(machine.videoSrc, options.apiBaseUrl)
  const videoHevc = watchSelectionVideoSrc
    ? ""
    : usesUiBranchMachineImage
      ? ""
      : normalizeGachaMediaUrl(machine.videoHevc, options.apiBaseUrl)
  const stockTotal = RARITIES.reduce(
    (total, rarity) => total + (machine.stock?.[rarity] ?? 0),
    0,
  )
  const isOpen = options.status?.isOpen ?? options.status?.status === "open"
  const configuredName = Object.hasOwn(MACHINE_NAMES, machine.code)
    ? MACHINE_NAMES[machine.code]
    : undefined

  return {
    code: machine.code,
    name: configuredName ?? machine.name,
    label: configuredName ?? (machine.shortName || machine.name),
    thumbnailUrl,
    videoSrc,
    videoHevc,
    price: machine.price ?? 0,
    priceLabel: formatUsd(machine.price),
    averageValueLabel: formatUsd(machine.ev),
    instantBuybackPercent: machine.instantBuyback ?? 0,
    turboMode: machine.turboMode ?? false,
    oddsTiers: RARITIES.map((rarity) => ({
      tierLabel: RARITY_LABELS[rarity],
      range: formatRange(machine.tierRanges?.[rarity]),
      chancePercent: formatPercent(machine.odds?.[rarity]),
      chancePercentClassName: RARITY_COLORS[rarity],
    })),
    commonStock: machine.stock?.common ?? 0,
    stockTotal,
    isOpen,
    isSoldOut: stockTotal <= 0,
    isPublic: machine.public ?? true,
  }
}

export function getNftAttribute(
  nft: GachaNftApi | undefined,
  traitType: string,
) {
  const attr = nft?.content?.metadata?.attributes?.find(
    (candidate) => candidate.trait_type === traitType,
  )
  return attr?.value === undefined ? undefined : String(attr.value)
}

function normalizeAttributeName(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "")
}

function getNftAttributeByNames(
  nft: GachaNftApi | undefined,
  traitTypes: readonly string[],
) {
  const normalizedTraitTypes = new Set(traitTypes.map(normalizeAttributeName))
  const attr = nft?.content?.metadata?.attributes?.find((candidate) => {
    if (!candidate.trait_type) return false
    return normalizedTraitTypes.has(
      normalizeAttributeName(candidate.trait_type),
    )
  })

  return attr?.value === undefined ? undefined : String(attr.value)
}

export function getNftName(nft: GachaNftApi | undefined) {
  return nft?.content?.metadata?.name || nft?.name || nft?.id || "Mystery item"
}

function getNftDisplayName(nft: GachaNftApi | undefined) {
  return (
    nft?.content?.metadata?.description || nft?.description || getNftName(nft)
  )
}

function getNftImage(nft: GachaNftApi | undefined) {
  const firstFile = nft?.content?.files?.[0]
  return (
    firstFile?.cc_cdn ||
    firstFile?.cdn_uri ||
    firstFile?.uri ||
    nft?.content?.links?.image ||
    nft?.image ||
    ""
  )
}

function getNftBackImage(nft: GachaNftApi | undefined) {
  const secondFile = nft?.content?.files?.[1]
  return (
    secondFile?.cc_cdn ||
    secondFile?.cdn_uri ||
    secondFile?.uri ||
    getNftImage(nft)
  )
}

function getNftValue(nft: GachaNftApi | undefined) {
  return (
    nft?.content?.metadata?.insuredValue ??
    nft?.insuredValue ??
    nft?.insured_value ??
    getNftAttribute(nft, "Insured Value")
  )
}

function getNftGrade(nft: GachaNftApi | undefined) {
  const company =
    getNftAttribute(nft, "Grading Company") ||
    nft?.content?.metadata?.gradingCompany
  const grade = getNftAttribute(nft, "The Grade")
  const gradeNum =
    getNftAttribute(nft, "GradeNum") || nft?.content?.metadata?.gradeNum

  if (company && grade) return `${company} ${grade}`
  if (company && gradeNum) return `${company} ${gradeNum}`
  return grade || gradeNum || "Authenticated"
}

function getNftCollectionName(nft: GachaNftApi | undefined) {
  return (
    nft?.collection?.name ||
    nft?.collectionName ||
    nft?.collection_name ||
    getNftAttribute(nft, "Collection") ||
    getNftAttribute(nft, "Set") ||
    ""
  )
}

function getNftCategoryName(nft: GachaNftApi | undefined) {
  return (
    nft?.categoryName ||
    nft?.category_name ||
    nft?.category ||
    nft?.content?.metadata?.categoryName ||
    nft?.content?.metadata?.category_name ||
    nft?.content?.metadata?.category ||
    getNftAttributeByNames(nft, ["Category"]) ||
    ""
  )
}

export function getGachaNftId(nft: GachaNftApi | undefined) {
  return (
    nft?.id ||
    nft?.nft_address ||
    nft?.mint ||
    nft?.mint_address ||
    nft?.tokenAddress ||
    ""
  )
}

export function getGachaNftAddress(nft: GachaNftApi | undefined) {
  return (
    nft?.nft_address ||
    nft?.mint ||
    nft?.mint_address ||
    nft?.tokenAddress ||
    nft?.id ||
    ""
  )
}

export function getGachaNftMintedAtMs(nft: GachaNftApi | undefined) {
  const timestamp =
    nft?.minted_at ||
    nft?.mintedAt ||
    nft?.mintedTime ||
    nft?.mintTime ||
    nft?.created_at ||
    nft?.createdAt ||
    nft?.content?.metadata?.minted_at ||
    nft?.content?.metadata?.mintedAt ||
    nft?.content?.metadata?.created_at ||
    nft?.content?.metadata?.createdAt ||
    getNftAttributeByNames(nft, [
      "Minted At",
      "Minted",
      "Mint Time",
      "Minted Time",
      "Created At",
      "Created",
    ])

  if (timestamp) {
    const parsed = new Date(timestamp).getTime()
    if (Number.isFinite(parsed)) return parsed
  }

  if (Number.isFinite(nft?.blockTime)) {
    return Number(nft?.blockTime) * 1000
  }

  return 0
}

export function getGachaNftReceivedAtMs(nft: GachaNftApi | undefined) {
  const timestamp =
    nft?.received_at ||
    nft?.receivedAt ||
    nft?.receivedTime ||
    nft?.owner_received_at ||
    nft?.ownerReceivedAt ||
    nft?.transferred_at ||
    nft?.transferredAt ||
    nft?.purchased_at ||
    nft?.purchasedAt ||
    nft?.opened_at ||
    nft?.openedAt ||
    nft?.updated_at ||
    nft?.updatedAt ||
    nft?.content?.metadata?.received_at ||
    nft?.content?.metadata?.receivedAt ||
    nft?.content?.metadata?.transferred_at ||
    nft?.content?.metadata?.transferredAt ||
    nft?.content?.metadata?.purchased_at ||
    nft?.content?.metadata?.purchasedAt ||
    nft?.content?.metadata?.opened_at ||
    nft?.content?.metadata?.openedAt ||
    getNftAttributeByNames(nft, [
      "Received At",
      "Received",
      "Owner Received At",
      "Transfer Time",
      "Transferred At",
      "Purchased At",
      "Opened At",
      "Pulled At",
    ])

  if (timestamp) {
    const parsed = new Date(timestamp).getTime()
    if (Number.isFinite(parsed)) return parsed
  }

  return 0
}

export function mapGachaNft(
  nft: GachaNftApi,
  options?: { machineCode?: string },
): GachaPrizeCard {
  const id = getGachaNftId(nft)
  const searchParams = new URLSearchParams()
  if (options?.machineCode) searchParams.set("machine", options.machineCode)
  const localHref = id
    ? `/gacha/items/${encodeURIComponent(id)}${searchParams.size ? `?${searchParams.toString()}` : ""}`
    : ""

  return {
    itemId: id,
    name: getNftDisplayName(nft),
    meta: getNftGrade(nft),
    price: formatInsuredValue(getNftValue(nft)),
    category: getNftCategoryName(nft),
    collection: getNftCollectionName(nft),
    setName: getNftAttribute(nft, "Set") ?? "",
    image: getNftImage(nft),
    href:
      localHref ||
      nft.content?.links?.external_url ||
      "https://collectorcrypt.com/assets/solana",
  }
}

export function mapGachaItemDetail(nft: GachaNftApi): GachaItemDetail {
  const id = getGachaNftId(nft)
  const attributes = nft.content?.metadata?.attributes ?? []
  const certNumber =
    nft.psaCertNumber ||
    nft.psa_cert_number ||
    nft.certificationNumber ||
    nft.certification_number ||
    nft.certNumber ||
    nft.cert_number ||
    nft.content?.metadata?.psaCertNumber ||
    nft.content?.metadata?.psa_cert_number ||
    nft.content?.metadata?.certificationNumber ||
    nft.content?.metadata?.certification_number ||
    nft.content?.metadata?.certNumber ||
    nft.content?.metadata?.cert_number ||
    getNftAttributeByNames(nft, [
      "PSA Cert Number",
      "PSA Cert",
      "PSA Sert Number",
      "PSA Sert",
      "PSA Certification Number",
      "PSA Certification",
      "Certification Number",
      "Certification",
      "Cert Number",
      "CertNumber",
      "Cert",
    ]) ||
    ""

  return {
    id,
    name: getNftDisplayName(nft),
    fullName: getNftDisplayName(nft),
    description: nft.content?.metadata?.description || nft.description || "",
    collectionName: getNftCollectionName(nft),
    categoryName: getNftCategoryName(nft),
    grade: getNftGrade(nft),
    value: formatInsuredValue(getNftValue(nft)),
    frontImageUrl: getNftImage(nft),
    backImageUrl: getNftBackImage(nft),
    certNumber,
    psaUrl: certNumber ? `https://www.psacard.com/cert/${certNumber}` : "",
    setName: getNftAttribute(nft, "Set") ?? "",
    year: getNftAttribute(nft, "Year") || "",
    parallel:
      nft.parallel ||
      nft.content?.metadata?.parallel ||
      getNftAttribute(nft, "Parallel") ||
      getNftAttribute(nft, "Variant") ||
      "",
    vault: nft.vault || getNftAttribute(nft, "Vault") || "",
    contractAddress: nft.contractAddress || id,
    ownerAddress: nft.ownerAddress || nft.owner_address || "",
    externalUrl:
      nft.content?.links?.external_url ||
      (id ? `https://collectorcrypt.com/assets/solana/${id}` : ""),
    attributes,
  }
}

function formatRelativeTime(value: string | undefined) {
  if (!value) return ""

  const timestamp = new Date(value).getTime()
  const diffMs = Date.now() - timestamp
  if (!Number.isFinite(diffMs) || diffMs < 60_000) return "Just now"

  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 60) return `${minutes} min ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`

  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? "" : "s"} ago`
}

export function mapGachaWinner(winner: GachaWinnerApi) {
  const nft = winner.nft
  const pull: GachaRecentPull = {
    name: getNftName(nft),
    value: formatInsuredValue(winner.insuredValue ?? getNftValue(nft)),
    collection: getNftCollectionName(nft),
    time: formatRelativeTime(winner.created_at),
    grade: getNftGrade(nft),
  }

  return {
    pull,
    image: getNftImage(nft),
    raw: winner,
  }
}

export function mapOpenPackResult(
  response: {
    nft_address?: string
    nftWon?: GachaNftApi
    buybackAmount?: number | string
    rarity?: string
    memo?: string
  },
  machine: GachaMachineView,
) {
  const nft = response.nftWon
  const rawMarketValue = getNftValue(nft)
  const itemFMV = normalizeUsdAmount(rawMarketValue)
  const marketValue = formatInsuredValue(rawMarketValue)
  const buybackAmount =
    response.buybackAmount !== undefined
      ? formatUsdWithCents(normalizeUsdAmount(response.buybackAmount))
      : formatUsdWithCents(
          ((Number(getNftValue(nft)) || machine.price) *
            machine.instantBuybackPercent) /
            100,
        )

  return {
    itemName: getNftDisplayName(nft),
    machineId: machine.code,
    machinePrice: machine.price,
    frontImageUrl: getNftImage(nft),
    backImageUrl: getNftBackImage(nft),
    marketValue,
    itemFMV,
    buybackOffer: buybackAmount,
    buybackRatePercent: machine.instantBuybackPercent,
    grade: getNftGrade(nft),
    collectionName: getNftCollectionName(nft),
    contractAddress: response.nft_address || "",
    memo: response.memo,
    nftAddress: response.nft_address,
    rarity: response.rarity,
  }
}
